import { useEffect, useRef } from 'react'
import { useFieldArray, UseFormReturn } from 'react-hook-form'
import type { FashionOrderFormValues, FashionOrderInstallment } from '../fashionOrder.schema'
import type { ProductApiData } from '../types'
import { addDays, buildInstallments, parseConditionToOffsets } from '../../../OrderForm/utils'
import { effectiveItemPrice } from '../../fashionOrderDetailFromApi'

const totalPraticado = (produtos: ProductApiData[], frete: number, orderSellingPrice?: number | null): number => {
  const hasAnyItemSellingPrice = produtos.some((p) => p.sellingPrice != null)
  if (!hasAnyItemSellingPrice && orderSellingPrice != null && Number(orderSellingPrice) > 0) {
    return Number(orderSellingPrice) + frete
  }
  return produtos.reduce((soma, produto) => soma + effectiveItemPrice(produto), 0) + frete
}

interface UseInstallmentsParams {
  form: UseFormReturn<FashionOrderFormValues>
  addedProducts: ProductApiData[]
  isEditing: boolean
}

export const useInstallments = ({ form, addedProducts, isEditing }: UseInstallmentsParams) => {
  const { control, getValues, setValue, watch, formState: { isDirty } } = form

  const { fields, append, replace } = useFieldArray({ control, name: 'installments', keyName: 'rhfId' })

  const installments = watch('installments')
  const paymentConditionName = watch('paymentConditionName')
  const shippingCost = watch('shippingCost')
  const deliveryDate = watch('deliveryDate')
  const sellingPrice = watch('sellingPrice')

  useEffect(() => {
    if (isEditing) return
    setValue('paymentMethodId', undefined)
    replace([])
  }, [isEditing, setValue, replace])

  const didInit = useRef(false)
  const ultimaCondicao = useRef(paymentConditionName)
  const ultimoTotal = useRef<number>(0)

  useEffect(() => {
    const shippingVal = Number(shippingCost || 0) / 100
    const totalOrder = totalPraticado(addedProducts, shippingVal, sellingPrice)

    if (isEditing && !didInit.current) {
      if (!paymentConditionName) return
      didInit.current = true
      ultimaCondicao.current = paymentConditionName
      ultimoTotal.current = totalOrder

      const currentInst = getValues('installments') || []
      const currentSum = currentInst.reduce((acc, i) => acc + Number(i?.valor || 0), 0)
      if ((currentSum === 0 || currentInst.length === 0) && totalOrder > 0) {
        const offsets = parseConditionToOffsets(paymentConditionName)
        const baseDate = deliveryDate || new Date().toISOString().slice(0, 10)
        replace(buildInstallments(totalOrder, offsets, baseDate))
      }
      return
    }

    const condicaoMudou = paymentConditionName !== ultimaCondicao.current
    ultimaCondicao.current = paymentConditionName

    if (!paymentConditionName) {
      if (condicaoMudou) replace([])
      return
    }

    const currentInst = getValues('installments') || []
    const currentSum = currentInst.reduce((acc, i) => acc + Number(i?.valor || 0), 0)
    const parcelasZeradas = (currentSum === 0 || currentInst.length === 0) && totalOrder > 0
    const totalMudou = Math.abs(totalOrder - ultimoTotal.current) > 0.01
    ultimoTotal.current = totalOrder

    if (!condicaoMudou && !parcelasZeradas && !totalMudou && !isDirty) return

    if (isEditing && !condicaoMudou && !parcelasZeradas && currentSum > 0 && Math.abs(currentSum - totalOrder) < 0.01) {
      return
    }

    const offsets = parseConditionToOffsets(paymentConditionName)
    const baseDate = deliveryDate || new Date().toISOString().slice(0, 10)
    replace(buildInstallments(totalOrder, offsets, baseDate))
  }, [paymentConditionName, addedProducts, shippingCost, deliveryDate, sellingPrice, isEditing, isDirty, replace, getValues])

  const handleUpdateInstallment = (index: number, fieldsToUpdate: Partial<FashionOrderInstallment>) => {
    Object.entries(fieldsToUpdate).forEach(([field, value]) => {
      setValue(`installments.${index}.${field as keyof FashionOrderInstallment}`, value)
    })
  }

  const handleAddInstallment = () => {
    const baseDate = deliveryDate || new Date().toISOString().slice(0, 10)
    append({
      numero: getValues('installments').length + 1,
      valor: 0,
      vencimento: baseDate,
      diasOffset: 0,
      comissaoAtiva: false,
    })
  }

  const handleRemoveInstallment = (index: number) => {
    const filtered = getValues('installments').filter((_, i) => i !== index)
    replace(filtered.map((item, idx) => ({ ...item, numero: idx + 1 })))
  }

  const handleRefreshDueDates = () => {
    if (!paymentConditionName) return
    const offsets = parseConditionToOffsets(paymentConditionName)
    const baseDate = deliveryDate || new Date().toISOString().slice(0, 10)
    const currentInst = getValues('installments') || []
    const currentSum = currentInst.reduce((acc, i) => acc + Number(i?.valor || 0), 0)
    if (currentSum === 0 || currentInst.length === 0 || Math.abs(currentSum - totalOrderAmount) > 0.01) {
      replace(buildInstallments(totalOrderAmount, offsets, baseDate))
      return
    }
    replace(
      currentInst.map((inst, index) => {
        const offset = offsets[index] ?? offsets[offsets.length - 1] ?? 0
        return {
          ...inst,
          vencimento: addDays(baseDate, offset),
          diasOffset: offset,
        }
      })
    )
  }

  const sumInstallments = installments.reduce((acc, inst) => acc + Number(inst.valor || 0), 0)
  const shippingVal = Number(shippingCost || 0) / 100
  const totalOrderAmount = totalPraticado(addedProducts, shippingVal, sellingPrice)
  const hasMismatch = installments.length > 0 && Math.abs(sumInstallments - totalOrderAmount) > 0.01

  return {
    installments,
    installmentFields: fields,
    handleUpdateInstallment,
    handleAddInstallment,
    handleRemoveInstallment,
    handleRefreshDueDates,
    sumInstallments,
    totalOrderAmount,
    hasMismatch,
  }
}
